import React, { useState, useRef, useEffect } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import Img from 'gatsby-image/withIEPolyfill';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FaTag } from 'react-icons/fa';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { TextField } from '@material-ui/core';

import { GatsbySanityImageFluid } from '../types';
import { usePagination, useUpdateUrlFromFilters, useOnLoadEffect } from '../utils/hooks';
import { truncateText, sortBy, convertRToSuperscript, flatten, uniq, mapBy } from '../utils/utils';
import PageLayout from '../components/PageLayout';
import ReadMoreLink from '../components/ReadMoreLink';
import SearchBox from '../components/SearchBox';
import EventsSection from '../components/EventsSection';
import NewsletterSection from '../components/NewsletterSection';
import DayPickerInput from '../components/DayPickerInput';

import * as filterStyles from '../components/FilterBox.module.scss';
import 'react-day-picker/lib/style.css';
import * as styles from './news.module.scss';
import GenericLink from '../components/GenericLink';
import SEO from '../components/SEO';
import NewsletterCTA from '../components/NewsletterCTA';
import NewsCard from '../components/NewsCard';

const NEWSLETTER_CTA_INDEX = 2;
const NEWSLETTER_CTA_PLACEHOLDER = 'NEWSLETTER_CTA' as const;
export interface Category {
  _id: string;
  title: string;
  slug: {
    current: string;
  };
}

export interface NewsArticle {
  _id: string;
  image?: {
    asset?: {
      fluid: GatsbySanityImageFluid;
    };
  };
  title: string;
  slug: {
    current: string;
  };
  date: string;
  categories: Array<Category>;
  extract?: string;
}

interface QueryData {
  site: {
    siteMetadata: {
      hasNewsletter: boolean;
    };
  };
  allSanityNewsArticle: {
    nodes: Array<NewsArticle>;
  };
  allSanityNewsCategory: {
    nodes: Array<Category>;
  };
  sanityHomepage: {
    events: Array<{
      _key: string;
    }>;
  };
}

type Filters = Record<'category', string | null>;

function parseDate(dateStr: string): Date {
  return new Date(dateStr + 'T00:00:00Z');
}

function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

const NewsArticleListPage = (): React.ReactElement => {
  const { t } = useTranslation();
  const data: QueryData = useStaticQuery(
    graphql`
      {
        site {
          siteMetadata {
            hasNewsletter
          }
        }
        allSanityNewsArticle {
          nodes {
            _id
            image {
              asset {
                fluid(maxWidth: 760, maxHeight: 420) {
                  ...GatsbySanityImageFluid
                }
              }
            }
            title
            slug {
              current
            }
            date
            categories {
              _id
              title
              slug {
                current
              }
            }
            extract
          }
        }
        allSanityNewsCategory {
          nodes {
            _id
            title
            slug {
              current
            }
          }
        }
        sanityHomepage {
          events {
            _key
          }
        }
      }
    `,
  );
  const { hasNewsletter } = data.site.siteMetadata;
  const homepage = data.sanityHomepage || {};
  const {
    allSanityNewsArticle: { nodes: allNews },
    allSanityNewsCategory: { nodes: allNewsCategories },
  } = data;

  const allNewsSorted = sortBy(allNews, newsArticle => parseDate(newsArticle.date), 'desc');
  const categoriesById = mapBy(allNewsCategories, '_id');

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  // BEGIN NEWSLETTER CTA LOGIC
  const newsletterSectionRef = useRef<HTMLElement>(null);
  function scrollToNewsletterSection() {
    if ('scroll' in window && newsletterSectionRef.current) {
      window.scroll({
        top: newsletterSectionRef.current.getBoundingClientRect().top + window.pageYOffset,
        behavior: 'smooth',
      });
    }
  }

  const [showNewsletterCTA, setShowNewsletterCTA] = useState(false);
  function closeNewsletterCTA() {
    window.localStorage.setItem('hiddenNewsletterCTA', 'true');
    setShowNewsletterCTA(false);
  }
  useEffect(() => {
    if (window.localStorage.getItem('hiddenNewsletterCTA') !== 'true') {
      setShowNewsletterCTA(true);
    }
  }, []);
  // END NEWSLETTER CTA LOGIC

  const [activeFilters, setActiveFilters] = useState<Filters>({
    category: null,
  });

  function updateFilters(filterName: string, value: string | null) {
    setActiveFilters({
      ...activeFilters,
      [filterName]: value,
    });
    resetPagination();
  }

  const dateFilteredNews = allNewsSorted
    .filter(newsArticle => !endDate || parseDate(newsArticle.date) <= endDate)
    .filter(newsArticle => !startDate || parseDate(newsArticle.date) >= startDate);

  const hasCategories = allNews.find(newsArticle => newsArticle.categories?.length > 0);
  const categoryIdsForFilter = uniq(
    flatten(dateFilteredNews.map(newsArticle => newsArticle.categories.map(cat => cat._id))),
  );

  const filteredNews = dateFilteredNews.filter(newsArticle =>
    activeFilters.category
      ? newsArticle.categories.map(cat => cat._id).includes(activeFilters.category)
      : true,
  );

  const {
    currentPage,
    currentPageItems: currentPageNews,
    getPageUrlPart,
    resetPagination,
    renderPagination,
  } = usePagination(
    [
      ...filteredNews.slice(0, NEWSLETTER_CTA_INDEX),
      ...(hasNewsletter && showNewsletterCTA ? [NEWSLETTER_CTA_PLACEHOLDER] : []),
      ...filteredNews.slice(NEWSLETTER_CTA_INDEX),
    ],
    6,
  );

  // Get initial filters state from url
  const hasRunOnLoadEffect = useOnLoadEffect(() => {
    let initialStartDate = null;
    let initialEndDate = null;
    let initialCategoryId = null;

    const categoriesBySlug = mapBy(allNewsCategories, category => category.slug.current);

    const currentUrl = new URLSearchParams(window.location.search);
    const urlStartDateSlug = currentUrl.get('from');
    const urlEndDateSlug = currentUrl.get('to');
    const urlCategorySlugEncoded = currentUrl.get('category');

    if (urlStartDateSlug && urlStartDateSlug.match(/^\d\d\d\d-\d\d-\d\d$/)) {
      initialStartDate = urlStartDateSlug;
    }
    if (urlEndDateSlug && urlEndDateSlug.match(/^\d\d\d\d-\d\d-\d\d$/)) {
      initialEndDate = urlEndDateSlug;
    }
    if (urlCategorySlugEncoded) {
      const initialCategory = categoriesBySlug[decodeURIComponent(urlCategorySlugEncoded)];
      initialCategoryId = initialCategory ? initialCategory._id : null;
    }
    setStartDate(initialStartDate ? parseDate(initialStartDate) : null);
    setEndDate(initialEndDate ? parseDate(initialEndDate) : null);
    setActiveFilters({
      category: initialCategoryId,
    });
  });

  // Update url on filters state change
  useUpdateUrlFromFilters(() => {
    const start = startDate ? formatDate(startDate) : null;
    const end = endDate ? formatDate(endDate) : null;
    const category = activeFilters.category ? categoriesById[activeFilters.category] : null;
    const categorySlugEncoded = category ? encodeURIComponent(category.slug.current) : null;
    return [
      ['from', start] as const,
      ['to', end] as const,
      ['category', categorySlugEncoded] as const,
      getPageUrlPart(),
    ];
  }, [
    startDate,
    endDate,
    ...sortBy(Object.entries(activeFilters), ([key]) => key).map(([, value]) => value),
    currentPage,
  ]);

  return (
    <>
      <PageLayout
        hasFilters
        title={t('news_page.title', 'News')}
        sideBarChildren={
          <div className={filterStyles.box}>
            {hasCategories && (
              <div className={filterStyles.group}>
                <span className={filterStyles.blueBordersLabel}>
                  {t('news_page.filters.category_title', 'Filter By Category')}
                </span>
                {hasRunOnLoadEffect && (
                  <div className={filterStyles.container}>
                    <Autocomplete
                      className={
                        filterStyles.input +
                        (activeFilters.category ? ' ' + filterStyles.inputSelected : '')
                      }
                      classes={{
                        listbox: filterStyles.selectListbox,
                        groupUl: filterStyles.selectGroupUl,
                        option: filterStyles.selectOption,
                      }}
                      disableClearable={!activeFilters.category}
                      disabled={categoryIdsForFilter.length === 0}
                      options={sortBy(
                        categoryIdsForFilter.map(
                          categoryId => categoriesById[categoryId] as Category,
                        ),
                        'title',
                      ).map(category => category._id)}
                      getOptionLabel={categoryId => {
                        if (!categoryId) {
                          return '';
                        }
                        return (categoriesById[categoryId] as Category).title;
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label={t('news_page.filters.category_label', 'Category')}
                        />
                      )}
                      value={activeFilters.category || undefined}
                      onChange={(_, value) => updateFilters('category', value || null)}
                    />
                  </div>
                )}
              </div>
            )}
            <div className={filterStyles.group}>
              <span className={filterStyles.blueBordersLabel}>
                {t('news_page.filters.title', 'Filter By Time Period')}
              </span>
              {hasRunOnLoadEffect && (
                <div className={filterStyles.container}>
                  <div className={filterStyles.input}>
                    <DayPickerInput
                      label={t('news_page.filters.start_date_label', 'Set date from')}
                      value={startDate || undefined}
                      onDayChange={d => {
                        setStartDate(d);
                        resetPagination();
                      }}
                      limitDate={endDate}
                      limitDirection="after"
                    ></DayPickerInput>
                  </div>
                  <div className={filterStyles.input}>
                    <DayPickerInput
                      label={t('news_page.filters.end_date_label', 'Set date to')}
                      value={endDate || undefined}
                      onDayChange={d => {
                        setEndDate(d);
                        resetPagination();
                      }}
                      limitDate={startDate}
                      limitDirection="before"
                    ></DayPickerInput>
                  </div>
                </div>
              )}
            </div>
          </div>
        }
        searchBox={
          <SearchBox<never, never>
            contentType="newsArticle"
            entriesGroups={[
              {
                entries: sortBy(allNews, 'title').map(newsArticle => ({
                  name: newsArticle.title,
                  url: '/news/' + newsArticle.slug.current,
                })),
              },
            ]}
            noResultsMessage={t(
              'news_page.search_box.no_search_results_text',
              'No news match the searched keyword.',
            )}
            renderNamePart={convertRToSuperscript}
          ></SearchBox>
        }
        extraSectionChildren={
          <>
            {!!homepage.events && homepage.events.length > 0 && <EventsSection></EventsSection>}
            {hasNewsletter && <NewsletterSection ref={newsletterSectionRef}></NewsletterSection>}
          </>
        }
      >
        <SEO title={t('news_page.title', 'News')} urlPath="/news" />
        {hasRunOnLoadEffect && (
          <div className={styles.container}>
            {currentPageNews.length === 0 ? (
              <div className={styles.noNewsResultsText}>
                <FontAwesomeIcon icon="info-circle" className={styles.infoIcon} />{' '}
                {t(
                  'news_page.filters.no_results_text',
                  'No news found for selected date interval.',
                )}
              </div>
            ) : (
              currentPageNews.map((article, i: number) =>
                article === NEWSLETTER_CTA_PLACEHOLDER ? (
                  <NewsletterCTA
                    onCloseClick={closeNewsletterCTA}
                    onSubscribeClick={scrollToNewsletterSection}
                    className={styles.card}
                  ></NewsletterCTA>
                ) : (
                  <NewsCard
                    className={styles.card}
                    article={article}
                    date={formatDate(parseDate(article.date))}
                    key={i}
                  />
                ),
              )
            )}
            {Array.from({ length: (3 - (data.allSanityNewsArticle.nodes.length % 3)) % 3 }).map(
              (__, i: number) => (
                <div key={i} className={styles.mockCard}></div>
              ),
            )}
          </div>
        )}
        {renderPagination()}
      </PageLayout>
    </>
  );
};

export default NewsArticleListPage;
